// Types
import {
    ConfiguredDiscount,
    SaveConfiguredDiscount,
    DiscountableType,
    ConfiguredDiscountItem,
    EditConfiguredDiscountItemAttribute,
    ConfiguredDiscountItemAttribute,
    ResourcesCount
} from '@spike/discount-model';
import { EnhancedProduct } from '../AddDiscount/hooks/useDiscountForm';

export interface ServicesContext {
    groomingServices: Array<{ id?: number }>;
    daycareServices: Array<{ id?: number }>;
    boardingServices: Array<{ id?: number }>;
    transportationServices: Array<{ id?: number }>;
    evaluationServices: Array<{ id?: number }>;
    addons: Array<{ id?: number }>;
    products: EnhancedProduct[];
}

/**
 * Converts a ConfiguredDiscount to SaveConfiguredDiscount format by removing read-only fields
 * and making id optional for API operations
 */
export const convertToSaveDiscount = (
    discount: ConfiguredDiscount,
    servicesContext: ServicesContext
): SaveConfiguredDiscount => {
    return {
        id: discount.id,
        name: discount.name,
        discountType: discount.discountType,
        values: discount.values,
        active: discount.active,
        customerTagList: discount.customerTagList,
        applicationType: discount.applicationType,
        configuredDiscountItemsAttributes: discount.items.map(item => ({
            discountableId: item.discountableItem.id,
            // Use the discountableType from the backend instead of recalculating it
            // This prevents race conditions when services/products haven't loaded yet
            discountableType: item.discountableType || getDiscountableType(item, servicesContext)
        }))
    };
};

/**
 * Helper to determine discountable type from item by checking which service list contains it
 */
const getDiscountableType = (item: ConfiguredDiscountItem, servicesContext: ServicesContext): DiscountableType => {
    const itemId = item.discountableItem.id;

    // Check if it's a product (using productId)
    const isProduct = servicesContext.products.some(p => p.businessProduct?.productId === itemId);
    if (isProduct) {
        return DiscountableType.BUSINESS_PRODUCT;
    }

    // Check if it's an addon
    const isAddon = servicesContext.addons.some(a => a.id === itemId);
    if (isAddon) {
        return DiscountableType.ADDON_SERVICE;
    }

    // Check if it's in any service category (grooming, daycare, boarding)
    const isService =
        servicesContext.groomingServices.some(s => s.id === itemId) ||
        servicesContext.daycareServices.some(s => s.id === itemId) ||
        servicesContext.boardingServices.some(s => s.id === itemId) ||
        servicesContext.transportationServices.some(s => s.id === itemId) ||
        servicesContext.evaluationServices.some(s => s.id === itemId);

    if (isService) {
        return DiscountableType.SERVICE;
    }

    // Default to SERVICE if not found (shouldn't happen in normal operation)
    return DiscountableType.SERVICE;
};

/**
 * Helper to determine apply-to label from values
 */
export const getApplyToLabel = (values: ConfiguredDiscount['values']): string => {
    if (!values) return 'Unknown';
    if (values.allPets !== undefined) return 'All Pets';
    if (values.additionalPets !== undefined) return 'Additional Pet';
    return 'Tiered';
};

/**
 * Counts discount items by business area, addons, and products
 * Returns an array of {businessArea, count} objects
 */
export const getBusinessAreaCounts = (
    discount: ConfiguredDiscount
): Array<{ businessArea: string; count: number; label: string }> => {
    const countsMap = new Map<string, number>();

    discount.items.forEach(item => {
        // Check discountable type first
        if (item.discountableType === DiscountableType.ADDON_SERVICE) {
            countsMap.set('addons', (countsMap.get('addons') || 0) + 1);
        } else if (item.discountableType === DiscountableType.BUSINESS_PRODUCT) {
            countsMap.set('products', (countsMap.get('products') || 0) + 1);
        } else if (item.discountableType === DiscountableType.SERVICE) {
            // For services, get the business area
            const businessAreaValue = item.discountableItem.businessArea;
            // Handle both string and Option<string> types
            let businessArea =
                typeof businessAreaValue === 'string' ? businessAreaValue : (businessAreaValue as { id?: string })?.id;

            // Merge dental services into grooming since they are now part of grooming
            if (businessArea === 'dental') {
                businessArea = 'grooming';
            }

            if (businessArea) {
                countsMap.set(businessArea, (countsMap.get(businessArea) || 0) + 1);
            }
        }
    });

    // Map IDs to readable labels
    const labels: Record<string, string> = {
        grooming: 'Grooming Services',
        daycare: 'Daycare Services',
        boarding: 'Boarding Services',
        transportation: 'Transportation Services',
        evaluation: 'Evaluation Services',
        addons: 'Add-ons',
        products: 'Products'
    };

    // Convert map to array with labels
    return Array.from(countsMap.entries()).map(([key, count]) => ({
        businessArea: key,
        count,
        label: labels[key] || key
    }));
};

/**
 * Formats business area display labels by comparing discount counts with marketplace totals.
 * Returns "All {Type}" if discount includes all available items, otherwise "{Type} ({count})"
 */
export const formatBusinessAreaLabels = (
    businessAreaCounts: Array<{ businessArea: string; count: number; label: string }>,
    resourcesCount: ResourcesCount | null
): Array<{ businessArea: string; displayLabel: string }> => {
    if (!resourcesCount) {
        // Fallback: show label with count if no resourcesCount available
        return businessAreaCounts.map(item => ({
            businessArea: item.businessArea,
            displayLabel: `${item.label} (${item.count})`
        }));
    }

    // Map business area keys to resourcesCount paths
    const getMaxCount = (businessArea: string): number | undefined => {
        switch (businessArea) {
            case 'grooming':
                // Dental services are now part of grooming, so combine both counts
                return (resourcesCount.services?.grooming || 0) + (resourcesCount.services?.dental || 0);
            case 'daycare':
                return resourcesCount.services?.daycare;
            case 'boarding':
                return resourcesCount.services?.boarding;
            case 'transportation':
                return resourcesCount.services?.transportation;
            case 'evaluation':
                return resourcesCount.services?.evaluation;
            case 'addons':
                return resourcesCount.addonServices;
            case 'products':
                return resourcesCount.businessProducts;
            default:
                return undefined;
        }
    };

    // Format labels for "All" prefix
    const allLabels: Record<string, string> = {
        grooming: 'All Grooming Services',
        daycare: 'All Daycare Services',
        boarding: 'All Boarding Services',
        transportation: 'All Transportation Services',
        evaluation: 'All Evaluation Services',
        addons: 'All Add-Ons',
        products: 'All Products'
    };

    return businessAreaCounts.map(item => {
        const maxCount = getMaxCount(item.businessArea);
        const isAll = maxCount !== undefined && item.count >= maxCount;

        return {
            businessArea: item.businessArea,
            displayLabel: isAll ? allLabels[item.businessArea] || `All ${item.label}` : `${item.label} (${item.count})`
        };
    });
};

/**
 * Converts SaveConfiguredDiscount to EditConfiguredDiscountItemAttribute[] for editing
 * Compares original items with current items to determine what needs to be added, kept, or deleted
 */
export const convertToEditItemsAttributes = (
    originalDiscount: ConfiguredDiscount,
    currentItemsAttributes: ConfiguredDiscountItemAttribute[]
): EditConfiguredDiscountItemAttribute[] => {
    const result: EditConfiguredDiscountItemAttribute[] = [];

    // Create a Set of current discountableIds for quick lookup
    const currentItemIds = new Set(currentItemsAttributes.map(item => item.discountableId));

    // Check original items - mark for deletion if not in current
    originalDiscount.items.forEach(originalItem => {
        const itemId = originalItem.discountableItem.id;

        if (!currentItemIds.has(itemId)) {
            // Item was removed - mark for deletion
            result.push({
                id: originalItem.id,
                _destroy: true
            });
        } else {
            // Item still exists - keep it (Rails will ignore unchanged)
            result.push({
                id: originalItem.id
            });
        }
    });

    // Create a Set of original item IDs for quick lookup
    const originalItemIds = new Set(originalDiscount.items.map(item => item.discountableItem.id));

    // Check current items - add new ones that weren't in original
    currentItemsAttributes.forEach(currentItem => {
        if (!originalItemIds.has(currentItem.discountableId)) {
            // New item - add without id
            result.push({
                discountableId: currentItem.discountableId,
                discountableType: currentItem.discountableType
            });
        }
    });

    return result;
};
